import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import apiClient from '@/api/client'
import type { ApiResponse } from '@/types/api'

interface Metrics {
  users: { total: number; new_this_week: number; verified: number }
  activity: { dau: number; mau: number; stickiness_percent: number; sessions_today: number }
  completion: { started: number; ended: number; rate_percent: number }
  subscriptions: { trial: number; active: number; past_due: number; conversion_percent: number }
}

/** Métriques de plateforme, réservées aux administrateurs (§ 10.6). */
export function PlatformMetrics() {
  const { t } = useTranslation()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () =>
      apiClient.get<ApiResponse<Metrics>>('/admin/metrics').then((r) => r.data.data),
    staleTime: 60_000,
  })

  if (isError) {
    return <p className="text-sm text-on-surface-variant">{t('admin.metrics.load_error')}</p>
  }

  if (isLoading || !data) {
    return <div className="h-24 animate-pulse rounded-2xl bg-surface-container" />
  }

  const cards = [
    ['dau', String(data.activity.dau)],
    ['mau', String(data.activity.mau)],
    ['stickiness', `${data.activity.stickiness_percent} %`],
    ['sessions_today', String(data.activity.sessions_today)],
    ['completion', `${data.completion.rate_percent} %`],
    ['conversion', `${data.subscriptions.conversion_percent} %`],
    ['new_users', String(data.users.new_this_week)],
    ['total_users', String(data.users.total)],
  ] as const

  return (
    <section aria-labelledby="admin-metrics-title" className="mb-8 flex flex-col gap-3">
      <h2 id="admin-metrics-title" className="font-display text-lg font-semibold text-on-surface">
        {t('admin.metrics.title')}
      </h2>

      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map(([key, value]) => (
          <div key={key} className="rounded-2xl bg-surface-low px-4 py-3">
            <dt className="text-xs font-medium text-on-surface-variant">
              {t(`admin.metrics.labels.${key}`)}
            </dt>
            <dd className="font-display text-xl font-bold tabular-nums text-on-surface">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
